import {
  asArray,
  getId,
  getLinkedRecord,
  getLinkedRecordItemId,
  getOperationItems,
  getRunSectionStepInputValues,
  readValue,
  requireSuccessfulServiceResponse
} from "../resourceConfigWizardUtils";

export const ENTITY_SCHEMA_MODE_MANUAL = "Manual";
export const ENTITY_SCHEMA_MODE_SOURCE = "Source";
export const ENTITY_SCHEMA_MODE_EDI = "EDI";
export const ENTITY_SCHEMA_MODE_DESTINATION_SCHEMA = "DestinationSchema";
export const CREATE_EDI_SCHEMA_OPERATION = "CreateEDISchema";
export const CREATE_ENTITY_SCHEMA_FROM_SOURCE_OPERATION = "CreateEntitySchemaFromSource";
export const CREATE_ENTITY_SCHEMA_FROM_DESTINATION_SCHEMA_OPERATION = "CreateEntitySchemaFromDestinationSchema";
export const EXTERNAL_SYSTEM_SYNC_PRODUCT_KEY = "ExternalSystemSync";

const DEFAULT_ENTITY_SCHEMA_SETUP_MODES = [
  {
    key: ENTITY_SCHEMA_MODE_MANUAL,
    label: "Build Your Own",
    description: "Create or link a Schema, then add and manage Entity Objects and fields manually."
  },
  {
    key: ENTITY_SCHEMA_MODE_SOURCE,
    label: "Start with a Source",
    description: "Select Source Objects to generate Entity Objects and fields from."
  },
  {
    key: ENTITY_SCHEMA_MODE_DESTINATION_SCHEMA,
    label: "Destination Schema",
    description: "Create or link a Schema and generate Entity Objects and fields from available External System destination schemas."
  },
  {
    key: ENTITY_SCHEMA_MODE_EDI,
    label: "EDI Schema",
    description: "Create or link a Schema and generate EDI Entity Objects from selected format, direction, and transaction sets."
  }
];

export const normalizeEntitySchemaMode = (mode) => {
  const value = String(mode || "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "");
  if (value === "source" || value === "startwithasource" || value === "startwithsource") return ENTITY_SCHEMA_MODE_SOURCE;
  if (value === "edi" || value === "edischema") return ENTITY_SCHEMA_MODE_EDI;
  if (value === "destinationschema" || value === "destination" || value === "externalsystemschema" || value === "externalsystem") return ENTITY_SCHEMA_MODE_DESTINATION_SCHEMA;
  return ENTITY_SCHEMA_MODE_MANUAL;
};

export const getEntitySchemaMode = (wizard, values = {}, options = {}) => {
  const currentValue = readValue(values, "SchemaSetupMode", "schemaSetupMode", "SetupMode", "setupMode");
  const savedSetupMode = readValue(
    getRunSectionStepInputValues(wizard, "configure", "setup-mode"),
    "SchemaSetupMode",
    "schemaSetupMode",
    "SetupMode",
    "setupMode"
  );
  return normalizeEntitySchemaMode(
    options.preferSavedSetupMode
      ? savedSetupMode || currentValue || readValue(getProductResourceSetupDefaults(wizard), "defaultSetupMode") || ENTITY_SCHEMA_MODE_MANUAL
      : currentValue || savedSetupMode || readValue(getProductResourceSetupDefaults(wizard), "defaultSetupMode") || ENTITY_SCHEMA_MODE_MANUAL
  );
};

export const getEntitySchemaSetupModes = (wizard) => {
  const defaults = getProductResourceSetupDefaults(wizard);
  const modes = asArray(readValue(defaults, "setupModes", "SetupModes"));
  const modesByKey = new Map(modes.map((mode) => [
    normalizeEntitySchemaMode(readValue(mode, "key", "Key", "value", "Value")),
    mode
  ]));
  const defaultKeys = new Set(DEFAULT_ENTITY_SCHEMA_SETUP_MODES.map((mode) => normalizeEntitySchemaMode(mode.key)));
  const orderedKnownModes = DEFAULT_ENTITY_SCHEMA_SETUP_MODES.map((defaultMode) => {
    const savedMode = modesByKey.get(normalizeEntitySchemaMode(defaultMode.key));
    return savedMode ? { ...defaultMode, ...savedMode } : defaultMode;
  });
  const customModes = modes.filter((mode) =>
    !defaultKeys.has(normalizeEntitySchemaMode(readValue(mode, "key", "Key", "value", "Value")))
  );
  return [...orderedKnownModes, ...customModes];
};

export const getEntitySchemaModeLabel = (wizard, mode) => {
  const normalizedMode = normalizeEntitySchemaMode(mode);
  const setupMode = getEntitySchemaSetupModes(wizard).find((entry) =>
    normalizeEntitySchemaMode(readValue(entry, "key", "Key", "value", "Value")) === normalizedMode
  );
  return readValue(setupMode, "label", "Label", "text", "Text") || normalizedMode;
};

export const getProductResourceSetupDefaults = (wizard) => {
  const productResource = readValue(wizard, "ProductResource", "productResource") || {};
  const defaultsJson = readValue(productResource, "SetupDefaultsJson", "setupDefaultsJson");
  if (!defaultsJson) return {};
  if (typeof defaultsJson === "object") return defaultsJson;
  try {
    return JSON.parse(defaultsJson);
  } catch {
    return {};
  }
};

export const getSchemaStepValues = (wizard, values = {}) => ({
  ...getRunSectionStepInputValues(wizard, "configure", "schema"),
  ...values
});

export const getLinkedSchemaId = (wizard) =>
  getLinkedRecordItemId(getLinkedRecord(wizard, "Schema", "EntitySchema")) || "";

export const getSchemaIdFromValues = (wizard, values = {}) =>
  readValue(values, "SchemaId", "schemaId", "id")
  || readValue(getSchemaStepValues(wizard), "SchemaId", "schemaId", "id")
  || getLinkedSchemaId(wizard)
  || "";

export const getSchemaDisplayName = (schema) =>
  readValue(schema, "Name", "name", "DisplayName", "displayName") || getId(schema) || "";

export const getActiveSchemaVersionId = (schema) => {
  const versions = asArray(readValue(schema, "SchemaVersions", "schemaVersions"));
  const primaryVersionId = readValue(schema, "ActiveSchemaVersionId", "activeSchemaVersionId");
  if (!primaryVersionId) return "";
  const primaryVersion = versions.find((version) =>
    normalizeId(readValue(version, "SchemaVersionId", "schemaVersionId", "UniqueId", "uniqueId")) === normalizeId(primaryVersionId)
  );
  return primaryVersion
    && readValue(primaryVersion, "IsPublished", "isPublished") === true
    && readValue(primaryVersion, "IsDeleted", "isDeleted") !== true
    && readValue(primaryVersion, "IsActive", "isActive") !== false
    ? primaryVersionId
    : "";
};

export const getEditableDraftSchemaVersionId = (schema) => {
  const versions = asArray(readValue(schema, "SchemaVersions", "schemaVersions"));
  const editableDrafts = versions.filter((version) =>
    readValue(version, "IsPublished", "isPublished") !== true
    && readValue(version, "IsDeleted", "isDeleted") !== true
    && readValue(version, "IsActive", "isActive") !== false
  );
  const draft = editableDrafts[editableDrafts.length - 1];
  return readValue(draft, "SchemaVersionId", "schemaVersionId", "UniqueId", "uniqueId") || "";
};

export const getEntityObjectDisplayName = (entityObject) =>
  readValue(entityObject, "DisplayName", "displayName", "ObjectName", "objectName", "Name", "name") || getId(entityObject) || "";

const option = (text, value = text) => ({ Text: text, Value: value });

const normalizeId = (value) => String(value ?? "").trim().toLowerCase();

const firstNonEmpty = (...values) =>
  values.map((value) => String(value ?? "").trim()).find(Boolean) || "";

const externalSystemRecordId = (externalSystem) =>
  firstNonEmpty(
    readValue(externalSystem, "_id", "id"),
    readValue(externalSystem, "ExternalSystemId", "externalSystemId"),
    readValue(externalSystem, "SchemaDestinationId", "schemaDestinationId"),
    readValue(externalSystem, "UniqueId", "uniqueId")
  );

const deploymentRecordId = (deployment) =>
  firstNonEmpty(
    readValue(deployment, "DeploymentId", "deploymentId"),
    readValue(deployment, "UniqueId", "uniqueId"),
    readValue(deployment, "_id", "id")
  );

const entityObjectRecordId = (entityObject) =>
  firstNonEmpty(
    readValue(entityObject, "EntityObjectId", "entityObjectId"),
    readValue(entityObject, "UniqueId", "uniqueId"),
    readValue(entityObject, "_id", "id")
  );

const linkedRecordMatchesExternalSystem = (record) => {
  const text = `${readValue(record, "ItemName", "itemName") || ""} ${readValue(record, "Relationship", "relationship") || ""}`.toLowerCase();
  return text.includes("externalsystem") || text.includes("external system") || text.includes("schemadestination") || text.includes("schema destination");
};

const matchesKnownId = (item, normalizedIds) => [
  getId(item),
  readValue(item, "_id", "id"),
  readValue(item, "UniqueId", "uniqueId"),
  readValue(item, "ExternalSystemId", "externalSystemId"),
  readValue(item, "SchemaDestinationId", "schemaDestinationId")
].some((candidate) => normalizedIds.has(normalizeId(candidate)));

const buildDestinationSchemaSelectionValue = ({
  externalSystem,
  deployment,
  entityObjectDeployment,
  entityObject
}) => {
  const externalSystemId = externalSystemRecordId(externalSystem);
  const deploymentId = deploymentRecordId(deployment);
  const entityObjectId = firstNonEmpty(
    readValue(entityObjectDeployment, "EntityObjectId", "entityObjectId"),
    entityObjectRecordId(entityObject)
  );
  const schemaVersionId = firstNonEmpty(
    readValue(deployment, "CurrentSchemaVersionId", "currentSchemaVersionId"),
    readValue(entityObject, "SchemaVersionId", "schemaVersionId")
  );
  const key = `destination_${externalSystemId}_${deploymentId}_${entityObjectId}`
    .replace(/[^A-Za-z0-9_-]+/g, "_")
    .replace(/^_+|_+$/g, "");

  return JSON.stringify({
    key,
    externalSystemId,
    deploymentId,
    schemaVersionId,
    entityObjectId,
    externalSystemName: readValue(externalSystem, "Name", "name", "DisplayName", "displayName") || "",
    deploymentName: readValue(deployment, "TargetName", "targetName", "SystemName", "systemName", "DeploymentName", "deploymentName") || "",
    entityObjectName: readValue(entityObject, "ObjectName", "objectName", "SystemName", "systemName") || "",
    entityObjectDisplayName: readValue(entityObject, "DisplayName", "displayName")
      || readValue(entityObjectDeployment, "EntityDisplayName", "entityDisplayName")
      || ""
  });
};

export const buildDestinationSchemaOptions = ({ externalSystems = [], entityObjects = [] }) => {
  const entityObjectsById = new Map();
  entityObjects.forEach((entityObject) => {
    [
      entityObjectRecordId(entityObject),
      readValue(entityObject, "EntityObjectId", "entityObjectId"),
      readValue(entityObject, "UniqueId", "uniqueId"),
      getId(entityObject)
    ].forEach((id) => {
      if (id) entityObjectsById.set(normalizeId(id), entityObject);
    });
  });

  const options = [];
  externalSystems.forEach((externalSystem) => {
    const externalSystemName = readValue(externalSystem, "Name", "name", "DisplayName", "displayName") || "External System";
    const deployments = asArray(readValue(externalSystem, "ExternalSystemDeployments", "externalSystemDeployments", "Deployments", "deployments"));
    const deploymentById = new Map(deployments.map((deployment) => [normalizeId(deploymentRecordId(deployment)), deployment]));
    const entityDeployments = asArray(readValue(externalSystem, "EntityObjectDeployments", "entityObjectDeployments"));
    entityDeployments
      .filter((entityObjectDeployment) => readValue(entityObjectDeployment, "IsDeleted", "isDeleted") !== true)
      .forEach((entityObjectDeployment) => {
        const deploymentId = firstNonEmpty(readValue(entityObjectDeployment, "SchemaDestinationDeploymentId", "schemaDestinationDeploymentId"));
        const deployment = deploymentById.get(normalizeId(deploymentId));
        if (!deployment || readValue(deployment, "IsActive", "isActive") === false || readValue(deployment, "IsDeleted", "isDeleted") === true) return;
        const entityObjectId = firstNonEmpty(readValue(entityObjectDeployment, "EntityObjectId", "entityObjectId"));
        const entityObject = entityObjectsById.get(normalizeId(entityObjectId));
        if (!entityObject || readValue(entityObject, "IsDeleted", "isDeleted") === true) return;
        const deploymentName = readValue(deployment, "TargetName", "targetName", "SystemName", "systemName", "DeploymentName", "deploymentName") || "";
        const entityObjectName = readValue(entityObject, "DisplayName", "displayName")
          || readValue(entityObjectDeployment, "EntityDisplayName", "entityDisplayName")
          || readValue(entityObject, "ObjectName", "objectName")
          || "Entity Object";
        const prefix = [externalSystemName, deploymentName].filter(Boolean).join(" ");
        options.push(option(
          `${prefix || externalSystemName} - ${entityObjectName}`,
          buildDestinationSchemaSelectionValue({ externalSystem, deployment, entityObjectDeployment, entityObject })
        ));
      });
  });

  const seen = new Set();
  return options
    .filter((entry) => {
      const key = normalizeId(entry.Value);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .sort((left, right) => left.Text.localeCompare(right.Text));
};

export const loadDestinationSchemaOptions = async ({
  externalSystemMgr,
  entityObjectMgr,
  resourceConfigurationMgr,
  coreState
}) => {
  if (!externalSystemMgr?.Query || !entityObjectMgr?.Query || !resourceConfigurationMgr?.Query) {
    throw new Error("External System resource services are not available.");
  }

  const configurationResult = await resourceConfigurationMgr.Query({
    model: "x => x.IsDeleted == false && x.ProductKey == productKey",
    params: { productKey: EXTERNAL_SYSTEM_SYNC_PRODUCT_KEY }
  });
  requireSuccessfulServiceResponse(configurationResult, "External System Sync resources could not be loaded.", coreState);

  const externalSystemIds = new Set();
  getOperationItems(configurationResult)
    .filter((configuration) => {
      const status = String(readValue(configuration, "ConfigurationStatus", "configurationStatus") || "").trim().toLowerCase();
      return status !== "retired" && status !== "6";
    })
    .flatMap((configuration) => asArray(readValue(configuration, "LinkedRecords", "linkedRecords")))
    .filter(linkedRecordMatchesExternalSystem)
    .forEach((record) => {
      const itemId = readValue(record, "ItemId", "itemId");
      if (itemId) externalSystemIds.add(normalizeId(itemId));
    });

  if (!externalSystemIds.size) return [];

  const externalSystemResult = await externalSystemMgr.Query({ model: "x => x.IsDeleted == false && x.IsActive == true" });
  requireSuccessfulServiceResponse(externalSystemResult, "External Systems could not be loaded.", coreState);
  const externalSystems = getOperationItems(externalSystemResult)
    .filter((externalSystem) => matchesKnownId(externalSystem, externalSystemIds));
  if (!externalSystems.length) return [];

  const entityObjectResult = await entityObjectMgr.Query({ model: "x => x.IsDeleted == false" });
  requireSuccessfulServiceResponse(entityObjectResult, "Destination Schema Entity Objects could not be loaded.", coreState);

  return buildDestinationSchemaOptions({
    externalSystems,
    entityObjects: getOperationItems(entityObjectResult)
  });
};
